import { formatDate, formatDateTime } from './dateUtils';

/**
 * Safely parse JSON string, returning undefined on failure
 * Returns undefined (not {}) so backend doesn't receive malformed data
 */
export const safeParseJSON = (jsonString) => {
  if (!jsonString || typeof jsonString !== 'string') return undefined;
  const trimmed = jsonString.trim();
  if (!trimmed) return undefined;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    console.warn('[safeParseJSON] Failed to parse:', jsonString, e);
    return undefined; // Return undefined so field is omitted from API request
  }
};

/**
 * Normalize URL to ensure it has a protocol (https://)
 * Backend validates URLs strictly and requires protocol
 */
export const normalizeUrl = (url) => {
  if (!url || typeof url !== 'string') return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;

  // Already has protocol
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Add https:// protocol
  return `https://${trimmed}`;
};

/**
 * Map frontend carrier type display to backend enum
 * @throws {Error} in development mode for unknown types
 */
export const mapCarrierTypeToBackend = (displayType) => {
  const mapping = {
    'Ocean Freight': 'ship',
    'Sea': 'ship',
    'Air Freight': 'air',
    'Air': 'air',
    'Rail': 'rail',
    'Road': 'road',
    'Logistics': 'road',
    'Freight Forwarding': 'ship',
  };

  const result = mapping[displayType];
  if (result === undefined) {
    const errorMsg = `[TRANSFORMER] Unknown carrier type: "${displayType}". Expected: ${Object.keys(mapping).join(', ')}`;
    console.error(errorMsg);

    if (import.meta.env.DEV) {
      throw new Error(errorMsg);
    }
    // Production: return undefined so callers can `.filter(Boolean)` it
    // out and surface a real error to the user instead of silently
    // miscategorising the carrier as ocean freight (the previous
    // fallback was 'ship', which masked typos and unknown-type carriers).
    return undefined;
  }
  return result;
};

/**
 * Map backend carrier type enum to frontend display
 */
export const mapCarrierTypeToDisplay = (backendType) => {
  const mapping = {
    'ship': 'Ocean Freight',
    'air': 'Air Freight',
    'rail': 'Rail',
    'road': 'Road',
  };
  return mapping[backendType] || backendType;
};

/**
 * Get icon for carrier type (frontend-only, not from API)
 */
export const getIconForType = (carrierType) => ({
  'ship': String.fromCodePoint(0x1F6A2),
  'air': String.fromCodePoint(0x2708, 0xFE0F),
  'rail': String.fromCodePoint(0x1F682),
  'road': String.fromCodePoint(0x1F69A)
}[carrierType] || String.fromCodePoint(0x1F4E6));

/**
 * Transform API carrier to frontend display format
 */
export const transformCarrierForDisplay = (apiCarrier) => {
  if (!apiCarrier || typeof apiCarrier !== 'object') {
    console.error('[transformCarrierForDisplay] Invalid carrier:', apiCarrier);
    return null;
  }

  // Use explicit contact_type from backend; fall back to inference for legacy carriers
  let contactType = apiCarrier.contact_type || 'email';
  let contactInfo = '';

  switch (contactType) {
    case 'api':
      contactInfo = apiCarrier.api || '';
      break;
    case 'website':
      contactInfo = apiCarrier.website || '';
      break;
    case 'phone':
      contactInfo = apiCarrier.contact_phone || '';
      break;
    case 'email':
    default:
      contactInfo = apiCarrier.email || '';
      break;
  }

  return {
    id: apiCarrier.id,
    name: apiCarrier.name,
    type: mapCarrierTypeToDisplay(apiCarrier.carrier_type),
    status: apiCarrier.status,
    email: apiCarrier.email,
    phone: apiCarrier.contact_phone, // Include phone for edit modal
    contactName: apiCarrier.contact_name, // Include contact name for edit modal
    contactType,
    contactInfo,
    icon: getIconForType(apiCarrier.carrier_type),
    // Include raw API data for edit modal
    website: apiCarrier.website,
    websiteUsername: apiCarrier.website_username,
    api: apiCarrier.api,
    apiHeaders: apiCarrier.api_headers,
  };
};

/**
 * Transform frontend form data to API format
 * Backend requires: name, carrier_type, email, contact_phone (10-20 chars), contact_name
 *
 * IMPORTANT: Only use contact data if that contact type is selected in contactTypes.
 * This ensures unchecking a contact type actually removes its data.
 */
export const transformCarrierForAPI = (formData) => {
  const selectedTypes = formData.contactTypes || [];

  // Email is required by backend - only use provided email if email type is selected
  const email = selectedTypes.includes('email') && formData.contacts?.email?.address?.trim()
    ? formData.contacts.email.address.trim()
    : `${formData.name?.toLowerCase().replace(/\s+/g, '.')}@carrier.local`;

  // Contact name - prefer from selected contact types, then fall back to carrier name
  let contactName = formData.name;
  if (selectedTypes.includes('email') && formData.contacts?.email?.name?.trim()) {
    contactName = formData.contacts.email.name.trim();
  } else if (selectedTypes.includes('phone') && formData.contacts?.phone?.name?.trim()) {
    contactName = formData.contacts.phone.name.trim();
  }

  // Phone is required by backend - only use provided phone if phone type is selected
  const contactPhone = selectedTypes.includes('phone') && formData.contacts?.phone?.number?.trim()
    ? formData.contacts.phone.number.trim()
    : '+10000000000';

  // Use the explicitly selected primary contact type
  const contactType = formData.primaryContactType || 'email';

  // Website and API - use empty string "" to clear fields
  // Backend: nil pointer = don't change, non-nil pointer to "" = clear the field
  return {
    name: formData.name,
    carrier_type: mapCarrierTypeToBackend(formData.type),
    contact_type: contactType,
    email,
    contact_phone: contactPhone,
    contact_name: contactName,
    website: selectedTypes.includes('website') ? normalizeUrl(formData.contacts?.website?.url) : '',
    website_username: selectedTypes.includes('website') ? (formData.contacts?.website?.username?.trim() || '') : '',
    website_password: selectedTypes.includes('website') ? (formData.contacts?.website?.password?.trim() || '') : '',
    api: selectedTypes.includes('api') ? normalizeUrl(formData.contacts?.api?.url) : '',
    api_headers: selectedTypes.includes('api') && formData.contacts?.api?.headers
      ? safeParseJSON(formData.contacts.api.headers)
      : null,
  };
};

/**
 * Map request status backend enum to frontend display
 */
export const mapRequestStatusToDisplay = (backendStatus) => ({
  'pending': 'Awaiting Response',
  'in_progress': 'Being processed',
  'completed': 'Ready',
  'cancelled': 'Cancelled',
  'accepted': 'Accepted',
  'carrier_declined': 'Not Accepted',
  'needs_clarification': 'Needs Clarification',
  'failed': 'Failed',
  'extracted': 'Review Required',
  'offline_followup': 'Offline Follow-up',
}[backendStatus] || backendStatus);

/**
 * Transform frontend RFQ form data to API format
 * Note: The backend expects structured data. The AI parsing will be done server-side.
 * For now, we send the raw RFQ text as prompt/description and use placeholder locations.
 */
export const transformRequestForAPI = (rfqText, carrierId) => {
  // Extract a title from the first line of the RFQ text or use a default
  const firstLine = rfqText.split('\n')[0].trim();
  const title = firstLine.length > 50
    ? firstLine.substring(0, 50) + '...'
    : firstLine || 'New Quote Request';

  // Set dates: requested date is today, deadline is 7 days from now
  const now = new Date();
  const deadline = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  return {
    title,
    description: rfqText,
    prompt: rfqText, // AI will process this field
    currency: 'AUD',
    requested_date: now.toISOString(),
    deadline: deadline.toISOString(),
    // Placeholder origin - will be parsed by AI
    origin: {
      address_line1: 'To be determined',
      city: 'Origin City',
      postal_code: '00000',
      country: 'US',
    },
    // Placeholder destination - will be parsed by AI
    destination: {
      address_line1: 'To be determined',
      city: 'Destination City',
      postal_code: '00000',
      country: 'US',
    },
    carrier_id: carrierId,
  };
};

/**
 * Transform API request to frontend display format
 */
export const transformRequestForDisplay = (apiRequest) => {
  if (!apiRequest || typeof apiRequest !== 'object') {
    console.error('[transformRequestForDisplay] Invalid request:', apiRequest);
    return null;
  }

  const quotedCurrency = apiRequest.quote_currency || apiRequest.currency || 'AUD';

  return {
    id: apiRequest.id,
    carrier: apiRequest.carrier_id,
    origin: `${apiRequest.origin_city || 'Unknown'}, ${apiRequest.origin_country || ''}`,
    destination: `${apiRequest.destination_city || 'Unknown'}, ${apiRequest.destination_country || ''}`,
    originFull: [
      apiRequest.origin_address_line1,
      apiRequest.origin_city,
      apiRequest.origin_state,
      apiRequest.origin_postal_code,
      apiRequest.origin_country,
    ].filter(Boolean).join(', '),
    destinationFull: [
      apiRequest.destination_address_line1,
      apiRequest.destination_city,
      apiRequest.destination_state,
      apiRequest.destination_postal_code,
      apiRequest.destination_country,
    ].filter(Boolean).join(', '),
    // Only use AI-extracted fields for shipment type — raw title is not a shipment type
    shipmentType: apiRequest.request_shipment_type || apiRequest.request_ai_title || null,
    commodity: apiRequest.request_commodity_type || apiRequest.description?.substring(0, 50) || '',
    fullDescription: apiRequest.description || '',
    submittedDate: formatDateTime(apiRequest.created_at),
    quotedPrice: apiRequest.quote_price != null ? apiRequest.quote_price : null,
    quotedPriceFormatted: apiRequest.quote_price != null
      ? `${quotedCurrency} ${apiRequest.quote_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : null,
    quoteIncludesGST: apiRequest.quote_includes_gst ?? null,
    validUntil: apiRequest.quote_expiry_date ? formatDate(apiRequest.quote_expiry_date) : null,
    status: mapRequestStatusToDisplay(apiRequest.status),
    type: (apiRequest.status === 'completed' || apiRequest.status === 'accepted') ? 'ready' : 'ongoing',
    carrierResponseNote: apiRequest.carrier_response_note || '',
    clarificationNotes: apiRequest.clarification_notes || '',
    clarificationReplySent: apiRequest.clarification_reply_sent === true,
    failureReason: apiRequest.failure_reason || '',
    // Per-user view state from the backend request-views table — drives the
    // "NEW" badge. lastSeenStatus === null means the current user has never
    // viewed this request; non-null means we recorded the status they saw.
    lastSeenAt: apiRequest.last_seen_at || null,
    lastSeenStatus: apiRequest.last_seen_status || null,
    // Request-origin cargo fields (AI-extracted from user RFQ)
    weightKg: apiRequest.request_weight_kg,
    volumeCBM: apiRequest.request_volume_cbm,
    quantity: apiRequest.request_quantity,
    packageType: apiRequest.request_package_type,
    lengthCM: apiRequest.request_length_cm,
    widthCM: apiRequest.request_width_cm,
    heightCM: apiRequest.request_height_cm,
    containerType: apiRequest.request_container_type,
    commodityType: apiRequest.request_commodity_type,
    cargoDescription: apiRequest.request_cargo_description,
    incoterms: apiRequest.request_incoterms,
    readyDate: apiRequest.request_ready_date ? formatDate(apiRequest.request_ready_date) : null,
    specialRequirements: apiRequest.request_special_requirements || '',
    stackable: apiRequest.request_stackable,
    dangerousGoods: apiRequest.request_dangerous_goods,
    forkliftAtOrigin: apiRequest.request_forklift_at_origin,
    forkliftAtDestination: apiRequest.request_forklift_at_destination,
    // Quote details
    transitDays: apiRequest.quote_transit_days,
    quoteTerms: apiRequest.quote_terms,
    // Metadata
    currency: apiRequest.currency || 'USD',
    quotedCurrency,
    deadline: apiRequest.deadline ? formatDate(apiRequest.deadline) : null,
    aiDescription: apiRequest.request_ai_description || '',
    quoteResponseText: apiRequest.quote_response_text || '',
    aiConfidence: apiRequest.request_ai_confidence,
    aiModel: apiRequest.ai_model,
    aiAssumptions: apiRequest.request_ai_assumptions ? (() => { try { return JSON.parse(apiRequest.request_ai_assumptions); } catch { return null; } })() : null,
    aiMissingFields: apiRequest.request_missing_fields ? (() => { try { return JSON.parse(apiRequest.request_missing_fields); } catch { return null; } })() : null,
    rawStatus: apiRequest.status,
    createdAt: apiRequest.created_at,
    updatedAt: apiRequest.updated_at,
  };
};
